/**
 * Vertical product thumbnail gallery: height sync, fade, and scroll arrows.
 */
(function () {
  const registry = new Map();

  function isMobile() {
    return window.innerWidth <= 768;
  }

  function isDesktopVertical() {
    return window.innerWidth >= 750;
  }

  function destroyGallery(sectionId) {
    const entry = registry.get(sectionId);
    if (!entry) return;

    if (entry.list && entry.onScroll) {
      entry.list.removeEventListener('scroll', entry.onScroll);
    }

    if (entry.listResizeObserver) entry.listResizeObserver.disconnect();
    if (entry.thumbResizeObserver) entry.thumbResizeObserver.disconnect();
    if (entry.resizeHandler) {
      window.removeEventListener('resize', entry.resizeHandler);
    }
    if (entry.syncTimeouts) {
      entry.syncTimeouts.forEach(clearTimeout);
    }

    registry.delete(sectionId);
  }

  function updateThumbnailScrollFade(mediaGallery) {
    try {
      if (!mediaGallery || !isDesktopVertical()) return;

      const thumbSlider = mediaGallery.querySelector('.lm-slider-thumb--vertical');
      if (!thumbSlider) return;

      const list = thumbSlider.querySelector('.thumbnail-list-lm');
      if (!list) return;

      const threshold = 2;
      const canScroll = list.scrollHeight > list.clientHeight + threshold;
      const prevBtn = thumbSlider.querySelector('.slider-button--prev');
      const nextBtn = thumbSlider.querySelector('.slider-button--next');

      if (!canScroll) {
        thumbSlider.classList.remove('is-fade-top', 'is-fade-bottom', 'is-arrow-top', 'is-arrow-bottom');
        if (prevBtn) prevBtn.setAttribute('disabled', 'disabled');
        if (nextBtn) nextBtn.setAttribute('disabled', 'disabled');
        return;
      }

      const atTop = list.scrollTop <= threshold;
      const atBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - threshold;
      const showTop = !atTop;
      const showBottom = !atBottom;
      const fadeEnabled = thumbSlider.dataset.showVerticalFade === 'true';

      if (fadeEnabled) {
        thumbSlider.classList.toggle('is-fade-top', showTop);
        thumbSlider.classList.toggle('is-fade-bottom', showBottom);
      } else {
        thumbSlider.classList.remove('is-fade-top', 'is-fade-bottom');
      }

      const arrowsEnabled = thumbSlider.dataset.showVerticalArrows === 'true';
      if (arrowsEnabled) {
        thumbSlider.classList.toggle('is-arrow-top', showTop);
        thumbSlider.classList.toggle('is-arrow-bottom', showBottom);

        if (prevBtn) {
          if (showTop) prevBtn.removeAttribute('disabled');
          else prevBtn.setAttribute('disabled', 'disabled');
        }
        if (nextBtn) {
          if (showBottom) nextBtn.removeAttribute('disabled');
          else nextBtn.setAttribute('disabled', 'disabled');
        }
      } else {
        thumbSlider.classList.remove('is-arrow-top', 'is-arrow-bottom');
      }
    } catch (error) {
      console.warn('Vertical thumbnail fade update failed:', error);
    }
  }

  function resetThumbnailHeight(mediaGallery) {
    const thumbnailSlider = mediaGallery?.querySelector('.lm-slider-thumb');
    if (!thumbnailSlider) return;

    thumbnailSlider.style.height = '';
    thumbnailSlider.classList.remove('height-synced');
  }

  function syncThumbnailHeight(mediaGallery) {
    try {
      if (!mediaGallery) return;

      if (isMobile()) {
        resetThumbnailHeight(mediaGallery);
        return;
      }

      const mainSlider = mediaGallery.querySelector('.lm-slider-main');
      const thumbnailSlider = mediaGallery.querySelector('.lm-slider-thumb');
      if (!mainSlider || !thumbnailSlider) return;

      const activeSlide = mainSlider.querySelector('.slider__slide.is-active');
      if (!activeSlide) return;

      const mainImage = activeSlide.querySelector('img');
      if (!mainImage) return;

      function setThumbnailHeight() {
        const containerWidth = mainSlider.offsetWidth;
        if (!containerWidth || !mainImage.naturalWidth) return;

        const aspectRatio = mainImage.naturalHeight / mainImage.naturalWidth;
        thumbnailSlider.style.height = containerWidth * aspectRatio + 'px';
        thumbnailSlider.classList.add('height-synced');
        updateThumbnailScrollFade(mediaGallery);
      }

      if (mainImage.complete) {
        setThumbnailHeight();
      } else {
        mainImage.addEventListener('load', setThumbnailHeight, { once: true });
      }
    } catch (error) {
      console.warn('Vertical thumbnail height sync failed:', error);
    }
  }

  function initVerticalGallery(mediaGallery) {
    try {
      if (!mediaGallery) return;

      const sectionId = mediaGallery.dataset.section;
      if (!sectionId) return;

      destroyGallery(sectionId);

    const thumbSlider = mediaGallery.querySelector('.lm-slider-thumb--vertical');
    const list = thumbSlider?.querySelector('.thumbnail-list-lm');
    if (!list) return;

    const entry = {
      mediaGallery,
      syncTimeouts: [],
      resizeHandler: null,
      listResizeObserver: null,
      thumbResizeObserver: null,
    };

    const onScroll = () => updateThumbnailScrollFade(mediaGallery);
    list.addEventListener('scroll', onScroll, { passive: true });
    entry.onScroll = onScroll;
    entry.list = list;

    if (window.ResizeObserver) {
      entry.listResizeObserver = new ResizeObserver(() => updateThumbnailScrollFade(mediaGallery));
      entry.listResizeObserver.observe(list);

      if (thumbSlider) {
        entry.thumbResizeObserver = new ResizeObserver(() => {
          syncThumbnailHeight(mediaGallery);
          updateThumbnailScrollFade(mediaGallery);
        });
        entry.thumbResizeObserver.observe(thumbSlider);
      }
    }

    entry.resizeHandler = () => {
      window.setTimeout(() => {
        if (isMobile()) {
          resetThumbnailHeight(mediaGallery);
        } else {
          syncThumbnailHeight(mediaGallery);
        }
        updateThumbnailScrollFade(mediaGallery);
      }, 100);
    };
    window.addEventListener('resize', entry.resizeHandler);

    registry.set(sectionId, entry);

    updateThumbnailScrollFade(mediaGallery);

    if (isMobile()) {
      resetThumbnailHeight(mediaGallery);
      return;
    }

    syncThumbnailHeight(mediaGallery);
    [50, 200, 500, 100, 500, 1200].forEach((delay) => {
      const id = window.setTimeout(() => {
        if (delay <= 500) {
          syncThumbnailHeight(mediaGallery);
        } else {
          updateThumbnailScrollFade(mediaGallery);
        }
      }, delay);
      entry.syncTimeouts.push(id);
    });

    entry.syncTimeouts.push(
      window.setTimeout(() => {
        if (isMobile()) return;
        const thumbnailSlider = mediaGallery.querySelector('.lm-slider-thumb');
        if (thumbnailSlider && !thumbnailSlider.classList.contains('height-synced')) {
          thumbnailSlider.style.height = '500px';
          thumbnailSlider.classList.add('height-synced');
          updateThumbnailScrollFade(mediaGallery);
        }
      }, 2000)
    );
    } catch (error) {
      console.warn('Vertical gallery init failed:', error);
    }
  }

  function initFromSectionId(sectionId) {
    if (!sectionId) return;
    window.requestAnimationFrame(() => {
      const mediaGallery = document.getElementById('MediaGallery-' + sectionId);
      if (mediaGallery?.querySelector('.lm-slider-thumb--vertical')) {
        initVerticalGallery(mediaGallery);
      }
    });
  }

  function initAll() {
    document.querySelectorAll('media-gallery').forEach((mediaGallery) => {
      if (mediaGallery.querySelector('.lm-slider-thumb--vertical')) {
        initVerticalGallery(mediaGallery);
      }
    });
  }

  if (!window.__verticalGalleryListenersReady) {
    window.__verticalGalleryListenersReady = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    } else {
      initAll();
    }

    document.addEventListener('shopify:section:load', (event) => {
      initFromSectionId(event.detail?.sectionId);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      destroyGallery(event.detail?.sectionId);
    });
  } else {
    initAll();
  }
})();
